/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, serial, text, integer, boolean, timestamp, jsonb, index, uuid } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { agents } from './agents';

/**
 * Chat Sessions - stores multi-agent chat session state and Honcho integration
 * 
 * Unlike agentSessions which are tied to a single agent, chatSessions can involve
 * multiple agents over the course of a conversation (e.g., skillshop_agent dispatching
 * to curriculum_designer, which then collaborates with assessment_designer, etc.)
 */
export const chatSessions = pgTable('chat_sessions', {
  id: serial('id').primaryKey(),
  
  // Basic session info
  userId: integer('user_id').notNull(), // Owner of the session
  currentAgentId: integer('current_agent_id').references(() => agents.id), // Currently active agent
  
  // External session identifiers
  sessionKey: text('session_key').notNull().unique(), // Internal session identifier
  
  // Honcho integration state
  honchoWorkspaceId: text('honcho_workspace_id'), // Honcho workspace ID
  honchoSessionId: text('honcho_session_id'), // Honcho session ID  
  honchoUserPeerId: text('honcho_user_peer_id'), // User's peer ID in Honcho
  
  // Session metadata
  title: text('title'), // Optional session title (auto-generated or user-set)
  description: text('description'), // Optional session description
  
  // UI State (persisted for session continuity)
  isExpanded: boolean('is_expanded').default(false),
  sidebarWidth: integer('sidebar_width').default(320),
  
  // Session context and metadata
  context: jsonb('context'), // Session-specific context
  metadata: jsonb('metadata'), // Additional metadata
  basePath: text('base_path'), // Base directory for resolving relative paths in MCP tools
  plan: jsonb('plan'), // Optional Plan object for task tracking
  
  // Session state
  isActive: boolean('is_active').default(true),
  isInitialized: boolean('is_initialized').default(false),
  
  // Timestamps
  startedAt: timestamp('started_at').defaultNow(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  lastActivityAt: timestamp('last_activity_at').defaultNow(),
  endedAt: timestamp('ended_at'),
  
  // Statistics (denormalized for performance)
  messageCount: integer('message_count').default(0),
  agentCount: integer('agent_count').default(0), // Number of different agents that participated
  totalTokensUsed: integer('total_tokens_used').default(0),
  totalProcessingTime: integer('total_processing_time').default(0), // in milliseconds
  toolCallsCount: integer('tool_calls_count').default(0),
  entityLinksCount: integer('entity_links_count').default(0),
}, (table) => ({
  userIdIdx: index('chat_sessions_user_idx').on(table.userId),
  sessionKeyIdx: index('chat_sessions_session_key_idx').on(table.sessionKey),
  honchoSessionIdx: index('chat_sessions_honcho_session_idx').on(table.honchoSessionId),
  currentAgentIdx: index('chat_sessions_current_agent_idx').on(table.currentAgentId),
  lastActivityIdx: index('chat_sessions_last_activity_idx').on(table.lastActivityAt),
}));

/**
 * Chat Messages - stores individual messages in chat sessions
 * Includes all the rich metadata from ChatMessage interface
 */
export const chatMessages = pgTable('chat_messages', {
  id: serial('id').primaryKey(),
  uuid: uuid('uuid').defaultRandom().notNull().unique(), // Client-stable UUID identifier
  
  // Session relationship
  sessionId: integer('session_id').notNull().references(() => chatSessions.id, { onDelete: 'cascade' }),
  
  // Message basics
  role: text('role').notNull(), // 'user' | 'assistant'
  content: text('content').notNull(),
  
  // Peer information (for multi-agent conversations)
  senderPeerId: text('sender_peer_id'), // Honcho peer ID of sender (user or agent)
  senderAgentId: integer('sender_agent_id').references(() => agents.id), // Agent ID if sender is an agent
  senderUserId: integer('sender_user_id'), // User ID if sender is a user
  
  // External IDs
  honchoMessageId: text('honcho_message_id'), // Honcho message ID for sync
  
  // Message order and timestamps
  sequenceNumber: integer('sequence_number').notNull(), // Order within session
  timestamp: timestamp('timestamp').defaultNow(),
  
  // Rich message data
  actionPlan: jsonb('action_plan'), // LearningPathActionPlan if present
  toolCalls: jsonb('tool_calls'), // ToolCall[] if present
  entityLinks: jsonb('entity_links'), // EntityLink[] if present
  
  // Execution state
  executionState: jsonb('execution_state'), // Action plan execution state
  
  // Message metadata
  metadata: jsonb('metadata'), // Agent info, model config, token counts, etc.
  
  // Performance metrics
  processingTime: integer('processing_time'), // milliseconds
  tokenCount: jsonb('token_count'), // {input, output, total}
  
  // Message state
  isDeleted: boolean('is_deleted').default(false),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  sessionIdIdx: index('chat_messages_session_idx').on(table.sessionId),
  sequenceIdx: index('chat_messages_sequence_idx').on(table.sessionId, table.sequenceNumber),
  honchoMessageIdx: index('chat_messages_honcho_idx').on(table.honchoMessageId),
  timestampIdx: index('chat_messages_timestamp_idx').on(table.timestamp),
  senderAgentIdx: index('chat_messages_sender_agent_idx').on(table.senderAgentId),
  uuidIdx: index('chat_messages_uuid_idx').on(table.uuid),
}));

/**
 * Chat Session Participants - tracks which agents have participated in a session
 * Useful for multi-agent conversations and session analytics
 */
export const chatSessionParticipants = pgTable('chat_session_participants', {
  id: serial('id').primaryKey(),
  
  sessionId: integer('session_id').notNull().references(() => chatSessions.id, { onDelete: 'cascade' }),
  agentId: integer('agent_id').notNull().references(() => agents.id),
  honchoAgentPeerId: text('honcho_agent_peer_id'), // Agent's peer ID in Honcho
  
  // Participation metrics
  messageCount: integer('message_count').default(0),
  firstMessageAt: timestamp('first_message_at'),
  lastMessageAt: timestamp('last_message_at'),
  
  // Participation state
  isActive: boolean('is_active').default(true),
  joinedAt: timestamp('joined_at').defaultNow(),
}, (table) => ({
  sessionAgentIdx: index('chat_session_participants_session_agent_idx').on(table.sessionId, table.agentId),
  sessionIdx: index('chat_session_participants_session_idx').on(table.sessionId),
  agentIdx: index('chat_session_participants_agent_idx').on(table.agentId),
}));

// Type exports
export type ChatSession = typeof chatSessions.$inferSelect;
export type InsertChatSession = typeof chatSessions.$inferInsert;
export type ChatMessage = typeof chatMessages.$inferSelect;
export type InsertChatMessage = typeof chatMessages.$inferInsert;
export type ChatSessionParticipant = typeof chatSessionParticipants.$inferSelect;
export type InsertChatSessionParticipant = typeof chatSessionParticipants.$inferInsert;

// Zod schemas for validation
export const insertChatSessionSchema = createInsertSchema(chatSessions);
export const selectChatSessionSchema = createSelectSchema(chatSessions);
export const insertChatMessageSchema = createInsertSchema(chatMessages);
export const selectChatMessageSchema = createSelectSchema(chatMessages);
export const insertChatSessionParticipantSchema = createInsertSchema(chatSessionParticipants);
export const selectChatSessionParticipantSchema = createSelectSchema(chatSessionParticipants);

// Extended schemas for API validation
export const createChatSessionSchema = insertChatSessionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastActivityAt: true,
});

export const createChatMessageSchema = insertChatMessageSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sequenceNumber: true, // Auto-generated
});

export const updateChatSessionSchema = insertChatSessionSchema.partial().omit({
  id: true,
  userId: true,
  createdAt: true,
});

// Response schemas for API
export const chatSessionResponseSchema = selectChatSessionSchema.extend({
  messages: z.array(selectChatMessageSchema).optional(),
  participants: z.array(selectChatSessionParticipantSchema).optional(),
});

export type ChatSessionResponse = z.infer<typeof chatSessionResponseSchema>;

// Add note about agentSessions deprecation
/**
 * Note: The existing agentSessions table should be considered deprecated for new chat functionality.
 * It can remain for backward compatibility with existing single-agent sessions, but new multi-agent
 * chat sessions should use the chatSessions table defined in this module.
 */
